import { NavLink } from "react-router-dom";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

import { useAuth } from "../auth/AuthContext";
import { useIsPilot } from "./PilotOnly";
import { useMyOpenCount } from "../data/useMyOpenCount";
import { AdminOnly } from "./AdminOnly";
import { Avatar } from "./Avatar";
import { HUB_URL } from "../config";
import { initialsOf } from "../../../src/shared/names";


interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Shown as a count pill; "hot" tints it. */
  count?: number;
  /** Match this route only when the path is exactly `to` (for index-like links). */
  end?: boolean;
}

// "From meetings" is intentionally absent until that screen is built.
const MY_WORK: NavItem = { to: "/my-work", label: "My work", icon: "my-work" };

const SECONDARY: NavItem[] = [
  { to: "/requests", label: "Requests", icon: "requests", end: true },
  { to: "/milestones", label: "Milestones", icon: "milestones" },
  { to: "/capacity", label: "Capacity", icon: "users" },
  { to: "/accounts", label: "Accounts", icon: "accounts" },
  { to: "/", label: "Dashboard", icon: "dashboard", end: true },
];

function itemClass({ isActive }: { isActive: boolean }): string {
  return [
    "flex h-8 items-center gap-2 rounded-md px-2 text-sm text-gray-700",
    "transition-colors hover:bg-gray-100 hover:text-[var(--text)] focus-visible:focus-ring",
    isActive ? "bg-gray-100 font-medium text-[var(--text)]" : "",
  ].join(" ");
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} end={item.end} className={itemClass}>
      <Icon name={item.icon} className="text-gray-500" />
      <span>{item.label}</span>
      {item.count !== undefined && <span className="ml-auto text-xs text-[var(--text-2)]">{item.count}</span>}
    </NavLink>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const pilot = useIsPilot();
  // The live count of open requests assigned to me; absent when unlinked.
  const myOpen = useMyOpenCount(user?.memberId);
  const primary: NavItem[] = [{ ...MY_WORK, count: myOpen }];

  return (
    <aside className="sticky top-0 flex h-dvh w-60 flex-none flex-col border-r border-[var(--border)] bg-[var(--bg)] p-3">
      <div className="mb-3 flex h-8 items-center px-2">
        <span className="text-sm font-semibold tracking-[-0.01em]">CE Hub</span>
      </div>

      {/* The ⌘K search is not built yet; the affordance is removed rather than
          left as a dead button. */}

      <nav aria-label="Main" className="flex flex-col gap-0.5">
        {primary.map((item) => (
          <NavItemLink key={item.to} item={item} />
        ))}
        <div className="my-3 h-px bg-[var(--border)]" />
        {SECONDARY.filter((item) => item.to !== "/capacity" || pilot).map((item) => (
          <NavItemLink key={item.to} item={item} />
        ))}
      </nav>

      <div className="mt-auto">
        {/* Settings is admin-only (§3.4). Hiding is not the control — the
            settings routes must refuse non-admins server side too — but a
            viewer should not see the affordance. */}
        <AdminOnly>
          <NavLink to="/settings/sla" className={itemClass}>
            <Icon name="settings" className="text-gray-500" />
            <span>Settings</span>
          </NavLink>
        </AdminOnly>

        {/* Same affordance as the other Hub apps: every one of them is a
            room off the Hub, and this is the door back. */}
        <a href={HUB_URL} className={itemClass({ isActive: false })}>
          <Icon name="chevron-left" className="text-gray-500" />
          <span>Back to Hub</span>
        </a>

        <div className="my-3 h-px bg-[var(--border)]" />

        <div className="flex items-center gap-2 px-2 py-1">
          <Avatar
            owner={{ name: user?.name ?? "You", initials: initialsOf(user?.name ?? "You"), color: "gray", avatarUrl: user?.avatarUrl }}
          />
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-sm font-medium leading-4">{user?.name ?? "You"}</span>
            <small className="text-xs text-[var(--text-2)]">{user?.appRole}</small>
          </span>
          <button
            type="button"
            onClick={logout}
            aria-label="Sign out"
            className="rounded-md p-1 text-[var(--text-2)] transition-colors hover:bg-gray-100 hover:text-[var(--text)] focus-visible:focus-ring"
          >
            <Icon name="log-out" />
          </button>
        </div>
      </div>
    </aside>
  );
}
